import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * PromoTicker — sticky red marquee for the Kingvention Early Bird bundle.
 * Auto-hides after the promo end date so no manual cleanup is needed.
 * While mounted it sets --pt-offset on <html> so the fixed header slides
 * down to sit under the ticker (see Header.jsx).
 */
const PROMO_END = new Date('2026-07-27T22:59:59.999Z'); // 23:59 UK BST on 27 July 2026

const TickerLine = () => (
  <>
    <span className="mx-6">EARLY BIRD ENDS 27 JULY</span>
    <span className="mx-6 text-white">·</span>
    <span className="mx-6">POPCROWN EXCLUSIVE BUNDLE — DANGEROUS: THE PARTY TICKET + OFFICIAL T-SHIRT</span>
    <span className="mx-6 text-white">·</span>
    <span className="mx-6">JOIN FREE TO CLAIM YOUR MEMBER CODE</span>
    <span className="mx-6 text-white">·</span>
    <span className="mx-6">FULL PRICE LIVE 28 JULY — NO DOOR SALES</span>
    <span className="mx-6 text-white">·</span>
  </>
);

const PromoTicker = () => {
  const [visible, setVisible] = useState(() => Date.now() < PROMO_END.getTime());
  const barRef = useRef(null);

  // Auto-hide once the deadline passes.
  useEffect(() => {
    if (!visible) return;
    const t = setInterval(() => {
      if (Date.now() >= PROMO_END.getTime()) setVisible(false);
    }, 60 * 1000);
    return () => clearInterval(t);
  }, [visible]);

  // Push the fixed header down while this banner is on screen.
  useLayoutEffect(() => {
    if (!visible) {
      document.documentElement.style.setProperty('--pt-offset', '0px');
      return undefined;
    }
    const setOffset = () => {
      const h = barRef.current?.offsetHeight || 0;
      document.documentElement.style.setProperty('--pt-offset', `${h}px`);
    };
    setOffset();
    window.addEventListener('resize', setOffset);
    return () => {
      window.removeEventListener('resize', setOffset);
      document.documentElement.style.setProperty('--pt-offset', '0px');
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={barRef}
      className="fixed top-0 left-0 right-0 z-[60] bg-black overflow-hidden"
      style={{
        borderTop: '1px solid #E10600',
        borderBottom: '1px solid #E10600',
        boxShadow: '0 4px 20px rgba(225, 6, 0, 0.35)',
      }}
      data-testid="promo-ticker-banner"
    >
      <Link
        to="/join"
        className="block group"
        aria-label="PopCrown Exclusive Kingvention Early Bird bundle ends 27 July — join to claim your code"
      >
        <div
          className="whitespace-nowrap py-2 text-[13px] md:text-sm font-extrabold tracking-wider text-[#E10600] uppercase group-hover:text-white transition-colors"
          style={{
            animation: 'popcrown-ticker 38s linear infinite',
            willChange: 'transform',
          }}
        >
          <span className="inline-block"><TickerLine /></span>
          <span className="inline-block"><TickerLine /></span>
          <span className="inline-block"><TickerLine /></span>
        </div>
      </Link>
      <style>{`
        @keyframes popcrown-ticker {
          from { transform: translateX(0); }
          to   { transform: translateX(-33.3333%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="promo-ticker-banner"] > a > div { animation-duration: 90s; }
        }
      `}</style>
    </div>
  );
};

export default PromoTicker;
